import React, { useCallback, useContext, useEffect, useMemo } from 'react';

import { AgentToolKit } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Toggle from '@amzn/meridian/toggle';

import { isNil } from 'lodash';
import { AgentContext } from 'src/contexts/AgentContext';
import { makeRows } from 'src/util/layout';

interface IToolSelector {}

export const ToolSelector: React.FC<IToolSelector> = (props) => {
    const {
        availableToolkits,
        loading,
        selectedToolkits: toolkits,
        setSelectedToolkits: setToolkits
    } = useContext(AgentContext);

    const allToolkits = useMemo(() => {
        return availableToolkits?.map((toolkit) => toolkit.gatewayId) ?? [];
    }, [availableToolkits]);

    const updateSelection = useCallback(
        (gatewayId: string, add: boolean) => {
            if (add) {
                setToolkits((prev) => {
                    if (isNil(prev) || prev.includes(gatewayId)) {
                        // do nothing
                        return prev;
                    } else {
                        return [...prev, gatewayId];
                    }
                });
            } else {
                setToolkits((prev) => {
                    if (isNil(prev)) {
                        return allToolkits.filter((_gatewayId) => _gatewayId !== gatewayId);
                    } else if (prev.includes(gatewayId)) {
                        return prev.filter((_gatewayId) => _gatewayId !== gatewayId);
                    } else {
                        // do nothing
                        return prev;
                    }
                });
            }
        },
        [toolkits, availableToolkits]
    );

    const loader = (
        <Row>
            <Loader type={'circular'} size={'small'} />
            <Text>Loading tools...</Text>
        </Row>
    );

    return (
        <Column>
            <Row alignmentHorizontal={'justify'}>
                <Text type={'h300'}>Agent Tools</Text>
                <Row alignmentHorizontal={'end'}>
                    <Button size={'small'} onClick={() => setToolkits(undefined)}>
                        Select All
                    </Button>
                    <Button type={'secondary'} size={'small'} onClick={() => setToolkits([])}>
                        Deselect All
                    </Button>
                </Row>
            </Row>
            {loading
                ? loader
                : makeRows(
                      2,
                      availableToolkits?.map((toolkit) => (
                          <ToolSelection
                              key={toolkit.gatewayId}
                              toolkit={toolkit}
                              selected={toolkits}
                              updateSelection={updateSelection}
                          />
                      ))
                  )}
        </Column>
    );
};

interface IToolSelection {
    toolkit: AgentToolKit;
    selected: string[] | undefined;
    updateSelection: (gatewayId: string, add: boolean) => void;
}

const ToolSelection: React.FC<IToolSelection> = (props) => {
    const { toolkit, selected, updateSelection } = props;

    const checked = useMemo(() => selected?.includes(toolkit.gatewayId) ?? true, [selected, toolkit]);

    const onChange = useCallback(
        (newChecked: boolean) => {
            updateSelection(toolkit.gatewayId, newChecked);
        },
        [toolkit]
    );

    return (
        <Box key={toolkit.gatewayId} type={'outline'} spacingInset={'400'}>
            <Column>
                <Column>
                    <Toggle checked={checked} onChange={onChange}>
                        {toolkit.gatewayName}
                    </Toggle>
                    <Text>{toolkit.description}</Text>
                </Column>
                <Box spacingInset={'none none none 400'}>
                    <Text tag={'ul'}>
                        {toolkit.toolDescriptions.map((desc) => (
                            <li key={desc.name}>
                                {desc.name}: {desc.description}
                            </li>
                        ))}
                    </Text>
                </Box>
            </Column>
        </Box>
    );
};
